/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useState } from 'react';
import { HiXMark, HiArrowDownTray, HiMusicalNote, HiDocument } from 'react-icons/hi2';
import { CgSpinner } from 'react-icons/cg';
import { StorageItem } from '../../../types/storage';
import { getFileExtension, getFileIcon } from '../../../utils/storageUtils';
import storageService from '../../../services/community/storageService';

interface FilePreviewModalProps {
  item: StorageItem | null;
  onClose: () => void;
}

type PreviewType = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'office' | 'none';

function getPreviewType(fileName: string): PreviewType {
  const ext = getFileExtension(fileName);
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'ogg'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'flac', 'aac', 'm4a'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if ([
    'txt', 'csv', 'md', 'json', 'xml', 'html', 'css',
    'js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'java', 'c',
    'cpp', 'cs', 'go', 'rs', 'php', 'sh', 'yaml', 'yml',
    'toml', 'ini', 'env', 'log',
  ].includes(ext)) return 'text';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'].includes(ext)) return 'office';
  return 'none';
}

export default function FilePreviewModal({ item, onClose }: FilePreviewModalProps): JSX.Element | null {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(false);

  useEffect(() => {
    if (!item) return;
    setTextContent(null);
    setTextError(false);

    if (getPreviewType(item.fileName) === 'text') {
      setTextLoading(true);
      storageService.getItemContent(item.id)
        .then((content) => {
          setTextContent(content);
          setTextLoading(false);
        })
        .catch(() => {
          setTextError(true);
          setTextLoading(false);
        });
    }
  }, [item?.id]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!item) return null;

  const previewType = getPreviewType(item.fileName);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-secondary-50 rounded-2xl shadow-2xl border border-secondary-200 flex flex-col overflow-hidden"
        style={{ width: '90vw', maxWidth: '1200px', height: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 flex-shrink-0 bg-secondary-50">
          <div className="flex items-center gap-3 min-w-0">
            {getFileIcon(item.fileName)}
            <span className="font-semibold text-primary-900 truncate">{item.fileName}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <a
              href={item.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-900 bg-secondary-200 rounded-lg hover:bg-secondary-200/70 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <HiArrowDownTray className="w-4 h-4" />
              Download
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-primary-900/50 hover:text-primary-900 hover:bg-secondary-200 rounded-lg transition-colors"
            >
              <HiXMark className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-6 bg-secondary-50">

          {previewType === 'image' && (
            <img
              src={item.fileUrl}
              alt={item.fileName}
              className="max-w-full max-h-full object-contain rounded-xl shadow-md"
            />
          )}

          {previewType === 'video' && (
            <video
              src={item.fileUrl}
              controls
              className="max-w-full max-h-full rounded-xl shadow-md"
            >
              Your browser does not support video playback.
            </video>
          )}

          {previewType === 'audio' && (
            <div className="flex flex-col items-center gap-6">
              <div className="w-32 h-32 rounded-full bg-secondary-200 flex items-center justify-center">
                <HiMusicalNote className="w-16 h-16 text-primary" />
              </div>
              <p className="text-primary-900 font-medium text-lg">{item.fileName}</p>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio src={item.fileUrl} controls className="w-80" />
            </div>
          )}

          {previewType === 'pdf' && (
            <iframe
              src={item.fileUrl}
              title={item.fileName}
              className="w-full h-full rounded-xl border border-secondary-200"
            />
          )}

          {previewType === 'text' && (
            textLoading ? (
              <CgSpinner className="animate-spin h-10 w-10 text-primary" />
            ) : textError ? (
              <div className="text-primary-900/50 text-sm">Could not load file content.</div>
            ) : (
              <pre className="w-full h-full overflow-auto text-sm text-primary-900 font-mono bg-secondary-50 rounded-xl p-6 border border-secondary-200 leading-relaxed">
                {textContent}
              </pre>
            )
          )}

          {previewType === 'office' && (
            /localhost|127\.0\.0\.1/.test(item.fileUrl) ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-24 h-24 rounded-full bg-secondary-200 flex items-center justify-center">
                  <HiDocument className="w-12 h-12 text-primary-900/30" />
                </div>
                <p className="text-primary-900 font-semibold text-lg">{item.fileName}</p>
                <p className="text-primary-900/50 text-sm">Office preview is not available in local development.</p>
                <a
                  href={item.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary-900 transition-colors"
                >
                  Open file
                </a>
              </div>
            ) : (
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(item.fileUrl)}`}
                title={item.fileName}
                className="w-full h-full rounded-xl border border-secondary-200"
              />
            )
          )}

          {previewType === 'none' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-24 h-24 rounded-full bg-secondary-200 flex items-center justify-center">
                <HiDocument className="w-12 h-12 text-primary-900/30" />
              </div>
              <p className="text-primary-900 font-semibold text-lg">{item.fileName}</p>
              <p className="text-primary-900/50 text-sm">No preview available for this file type.</p>
              <a
                href={item.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary-900 transition-colors"
              >
                Open file
              </a>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
